import React, { useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { Outlet, useNavigate } from "react-router-dom";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ExitToAppOutlinedIcon from "@mui/icons-material/ExitToAppOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import SettingsBrightnessIcon from "@mui/icons-material/SettingsBrightness";
import WbSunnyOutlinedIcon from "@mui/icons-material/WbSunnyOutlined";
import NightsStayOutlinedIcon from "@mui/icons-material/NightsStayOutlined";
import { useDispatch, useSelector } from "react-redux";
import {
  logout,
  selectCurrentUser,
  selectPromptEmailUpdate,
} from "../pages/auth/authSlice";
import { setColorMode, selectColorMode } from "../../theme/themeSlice";
import { authApiSlice } from "../../app/authApiSlice";
import ChangePasswordDialog from "../pages/dashboard/ChangePasswordDialog";
import ParentEmailPromptDialog from "../pages/auth/ParentEmailPromptDialog";
import { formatTelLink, formatWhatsappLink } from "../../helpers/phoneUtils";
import { useGetSchoolAgentQuery } from "../pages/settings/school/schoolApiSlice";
import { SupportAgent } from "@mui/icons-material";

const DEFAULT_CONTACT_PHONE = "+234 8088 885 123";

function ParentLayout() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const parentHeaderBg = isDark ? "#1e1e1e" : theme.palette.primary.main;
  const parentHeaderText = isDark
    ? "#fff"
    : theme.palette.getContrastText(parentHeaderBg);
  const user = useSelector(selectCurrentUser);
  const promptEmailUpdate = useSelector(selectPromptEmailUpdate);
  const colorMode = useSelector(selectColorMode);

  const { data: agent } = useGetSchoolAgentQuery();
  const agentPhone = agent?.profile?.phone || DEFAULT_CONTACT_PHONE;

  const CONTACT_INFO = {
    phone: {
      display: agentPhone,
      link: formatTelLink(agentPhone),
      icon: "📞",
    },
    whatsapp: {
      display: agentPhone,
      link: formatWhatsappLink(agentPhone),
      icon: "💬",
    },
    email: {
      display: agent?.profile?.email || "support@schoola.app",
      link: agent?.profile?.email
        ? `mailto:${agent.profile.email}`
        : "mailto:support@schoola.app",
      icon: "✉️",
    },
  };
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [helpAnchor, setHelpAnchor] = useState(null);
  const helpOpen = Boolean(helpAnchor);

  const modeSequence = ["system", "light", "dark"];
  const handleModeToggle = () => {
    const next =
      modeSequence[(modeSequence.indexOf(colorMode) + 1) % modeSequence.length];
    dispatch(setColorMode(next));
  };
  const modeIcon =
    colorMode === "light" ? (
      <WbSunnyOutlinedIcon fontSize="small" />
    ) : colorMode === "dark" ? (
      <NightsStayOutlinedIcon fontSize="small" />
    ) : (
      <SettingsBrightnessIcon fontSize="small" />
    );
  const modeLabel =
    colorMode === "light"
      ? "Light mode"
      : colorMode === "dark"
        ? "Dark mode"
        : "System default";

  const handleHelpOpen = (event) => {
    setHelpAnchor(event.currentTarget);
  };

  const handleHelpClose = () => {
    setHelpAnchor(null);
  };

  const handleExit = () => {
    const nonParentRoles = user?.roles?.filter(
      (r) => r?.role?.name && r.role.name !== "parent",
    );
    if (nonParentRoles?.length) {
      // User has other roles — return to the main dashboard
      navigate("/dashboard");
    } else {
      // Parent-only account — log out entirely
      dispatch(logout());
      dispatch(authApiSlice.util.resetApiState());
      navigate("/login");
    }
  };

  return (
    <Box
      sx={{ minHeight: "100vh", background: isDark ? "#121212" : "#F7F7F8" }}
    >
      <AppBar
        position="fixed"
        sx={{
          boxShadow: "none",
          backgroundColor: parentHeaderBg,
          borderBottom: isDark ? "1px solid #333" : "none",
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 0.5,
            minHeight: { xs: 56, sm: 64 },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              startIcon={<HomeOutlinedIcon />}
              onClick={() => navigate("/parent")}
              sx={{ color: parentHeaderText, textTransform: "none" }}
            >
              Parent Portal
            </Button>
            <Button
              startIcon={<ForumOutlinedIcon />}
              onClick={() => navigate("/communication/feed")}
              sx={{
                color: parentHeaderText,
                textTransform: "none",
                display: { xs: "none", sm: "inline-flex" },
              }}
            >
              Connect
            </Button>
          </Box>
          <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
            <Tooltip title="Timeline, messages, and notification settings">
              <IconButton
                onClick={() => navigate("/communication/feed")}
                size="small"
                sx={{ color: parentHeaderText }}
              >
                <NotificationsOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={modeLabel}>
              <IconButton
                onClick={handleModeToggle}
                size="small"
                sx={{ color: parentHeaderText }}
              >
                {modeIcon}
              </IconButton>
            </Tooltip>
            <Tooltip title="Help">
              <IconButton
                onClick={handleHelpOpen}
                size="small"
                sx={{ color: parentHeaderText }}
              >
                <SupportAgent />
              </IconButton>
            </Tooltip>
            <Button
              startIcon={<LockOutlinedIcon />}
              onClick={() => setChangePasswordOpen(true)}
              sx={{
                color: parentHeaderText,
                textTransform: "none",
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
              }}
            >
              <Box
                component="span"
                sx={{ display: { xs: "none", sm: "inline" } }}
              >
                Change Password
              </Box>
              <Box
                component="span"
                sx={{ display: { xs: "inline", sm: "none" } }}
              >
                Password
              </Box>
            </Button>
            <Button
              startIcon={<ExitToAppOutlinedIcon />}
              onClick={handleExit}
              sx={{
                color: parentHeaderText,
                textTransform: "none",
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
              }}
            >
              Exit
            </Button>
          </Box>
        </Toolbar>

        <Menu
          anchorEl={helpAnchor}
          open={helpOpen}
          onClose={handleHelpClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MenuItem disabled sx={{ cursor: "default" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Need Help?
            </Typography>
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={() => {
              handleHelpClose();
              window.location.href = CONTACT_INFO.phone.link;
            }}
            sx={{ fontSize: "0.9rem" }}
          >
            {CONTACT_INFO.phone.icon} Phone: {CONTACT_INFO.phone.display}
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleHelpClose();
              window.open(CONTACT_INFO.whatsapp.link, "_blank");
            }}
            sx={{ fontSize: "0.9rem" }}
          >
            {CONTACT_INFO.whatsapp.icon} WhatsApp:{" "}
            {CONTACT_INFO.whatsapp.display}
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleHelpClose();
              window.location.href = CONTACT_INFO.email.link;
            }}
            sx={{ fontSize: "0.9rem" }}
          >
            {CONTACT_INFO.email.icon} Email: {CONTACT_INFO.email.display}
          </MenuItem>
        </Menu>
      </AppBar>

      <Box
        component="main"
        sx={{
          pt: 8,
          px: { xs: 2, sm: 3 },
          pb: 4,
          maxWidth: 1200,
          mx: "auto",
        }}
      >
        <Outlet />
      </Box>

      <ChangePasswordDialog
        open={changePasswordOpen}
        setOpen={setChangePasswordOpen}
      />

      <ParentEmailPromptDialog open={!!promptEmailUpdate} />
    </Box>
  );
}

export default ParentLayout;
